import { useIsomorphicLayoutEffect } from '@doordash/utilities';
import { IconColor, IconSize, IconType } from './constants.js';
import { getIconBrand, iconAvailabilityKey, createSvgUrl, svgErrorHandler, svgAttrUpdates } from './utils.js';
import { StyledInlineSvg } from './styles.js';
import FallbackIcon from './FallbackIcon.js';
import LoadingPlaceholder from './LoadingPlaceholder.js';
import React from 'react';
import { Brands } from '../theming/constants.js';
import '../theming/themes/caviar.js';
import '../theming/themes/merchant.js';
import '../theming/themes/merchant-next.js';
import '../theming/themes/internal-tools.js';
import { ThemingContext } from '../theming/theming.js';
import '../theming/typography/constants.js';
import 'lodash.clonedeep';
import 'lodash.merge';

function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

function _slicedToArray(arr, i) { return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest(); }

function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

function _iterableToArrayLimit(arr, i) { var _i = arr && (typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"]); if (_i == null) return; var _arr = []; var _n = true; var _d = false; var _s, _e; try { for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"] != null) _i["return"](); } finally { if (_d) throw _e; } } return _arr; }

function _arrayWithHoles(arr) { if (Array.isArray(arr)) return arr; }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

var generateAriaProps = function generateAriaProps(label, description) {
  var hasInvalidLabel = label === null || typeof label !== 'string' || label.length === 0;
  return {
    'aria-describedby': description,
    'aria-hidden': hasInvalidLabel,
    role: hasInvalidLabel ? 'presentation' : undefined
  };
};

var Icon = function Icon(_ref) {
  var _ref$color = _ref.color,
      color = _ref$color === void 0 ? IconColor.SystemBlack : _ref$color,
      _ref$size = _ref.size,
      size = _ref$size === void 0 ? IconSize.Medium : _ref$size,
      _ref$type = _ref.type,
      type = _ref$type === void 0 ? IconType.StarFill : _ref$type,
      _ref$accessibilityLab = _ref.accessibilityLabel,
      accessibilityLabel = _ref$accessibilityLab === void 0 ? '' : _ref$accessibilityLab,
      _ref$useDefaultColors = _ref.useDefaultColors,
      useDefaultColors = _ref$useDefaultColors === void 0 ? false : _ref$useDefaultColors,
      accessibilityDescribedBy = _ref.accessibilityDescribedBy,
      shouldAdjustSmallSizesWhitespace = _ref.shouldAdjustSmallSizesWhitespace,
      props = _objectWithoutProperties(_ref, ["color", "size", "type", "accessibilityLabel", "useDefaultColors", "accessibilityDescribedBy", "shouldAdjustSmallSizesWhitespace"]);

  var theme = React.useContext(ThemingContext);
  var iconBrandToUse = getIconBrand(theme.Icons.Brand, type);
  var iconKey = iconAvailabilityKey({
    brand: iconBrandToUse,
    size: size,
    type: type
  });
  var svgUrl = createSvgUrl(iconBrandToUse, iconKey);
  var iconColor = theme.Colors[color] || color;

  var _React$useState = React.useState(false),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      svgFetchError = _React$useState2[0],
      setSvgFetchError = _React$useState2[1];

  var _React$useState3 = React.useState(false),
      _React$useState4 = _slicedToArray(_React$useState3, 2),
      domReady = _React$useState4[0],
      setDomReady = _React$useState4[1];

  useIsomorphicLayoutEffect(function () {
    setDomReady(true);
  }, []);

  if (!iconBrandToUse) {
    return /*#__PURE__*/React.createElement("div", null, "Unknown icon");
  } // If there is an issue with fetching the icon SVG from the CDN or S3,
  // fallback to a "missing icon" icon to:
  // 1) ensure that there aren't a large number of blank spaces across project designs
  // 2) visually surface icon issue to developers


  if (svgFetchError) {
    return /*#__PURE__*/React.createElement(FallbackIcon, _extends({}, generateAriaProps(accessibilityLabel, accessibilityDescribedBy), {
      size: size,
      iconColor: iconColor,
      useDefaultColors: useDefaultColors
    }));
  } // We are rendering SVG only when DOM is ready as `react-inlinesvg` doesn't function until
  // DOM is available hence causing the client/server HTML mismatch error during SSR hydration.


  return domReady ? /*#__PURE__*/React.createElement(StyledInlineSvg // Note: the key is necessary to ensure the preProcessor function runs on every prop update
  // From https://github.com/gilbarbara/react-inlinesvg/issues/120
  , _extends({
    key: "".concat(size, "-").concat(type, "-").concat(color, "-").concat(useDefaultColors),
    loader: /*#__PURE__*/React.createElement(LoadingPlaceholder, {
      size: size
    }),
    onError: function onError(error) {
      return svgErrorHandler(error, setSvgFetchError);
    },
    preProcessor: function preProcessor(code) {
      return svgAttrUpdates({
        code: code,
        iconColor: iconColor,
        useDefaultColors: useDefaultColors
      });
    },
    title: accessibilityLabel,
    src: svgUrl,
    "aria-hidden": accessibilityLabel === null || typeof accessibilityLabel !== 'string' || accessibilityLabel.length === 0,
    "aria-describedby": accessibilityDescribedBy,
    width: size,
    height: size,
    $shouldScaleDownIcon: // This prop is being used to scale the new icon assets with 16x16px artboards down
    // in order to prevent a breaking change. This will only be true when using a small (16px)
    // icon, and the prop or theme value for shouldAdjustSmallSizesWhitespace is false.
    iconKey.slice(0, 2) === '16' && (typeof shouldAdjustSmallSizesWhitespace !== 'undefined' ? !shouldAdjustSmallSizesWhitespace : !theme.Icons.shouldAdjustSmallSizesWhitespace)
  }, props)) : /*#__PURE__*/React.createElement(LoadingPlaceholder, {
    size: size
  });
};

Icon.Brands = Brands;
Icon.Sizes = IconSize;
Icon.Colors = IconColor;
Icon.Types = IconType;

export { Icon as default };
